import React, { useMemo, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import { useOrderDetailContext } from 'providers/OrderDetailProvider';
import { useUserContext } from 'providers/UserProvider';

import Table from 'components/common/Table';
import OrderShipmentExpandedRow from 'components/add_order/order/OrderDetail/OrderShipments/OrderShipmentExpandedRow';

import { shipmentApi } from 'api';
import { getOrderDetailShipmentColumns } from 'constants/order';
import { PRINTABLE_INVOICE_STATUSES } from 'constants/shipment';
import {
  convertPdfToHtml,
  showAwbPdf,
  generateInvoicePdf,
  generateBlueDartAwbPdf,
} from 'utils/pdfGenerators';

const OrderShipments = ({ orderId }) => {
  const tableRef = useRef();

  const { orderDetailData } = useOrderDetailContext();
  const { selectedTenant, selectedWarehouse } = useUserContext();
  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;

  const handleRefreshShipment = async shipmentId => {
    try {
      await shipmentApi.refreshShipment(shipmentId);

      toast.success('Successfully generated AWB');
    } catch (error) {
      toast.error('Failed to generate AWB');
    } finally {
      tableRef.current?.refetch();
    }
  };

  const handlePrintPdf = async shipmentId => {
    try {
      const response = await shipmentApi.getInvoiceDetail(shipmentId);

      const shipmentAWBFile = response?.shipment?.awb_file;
      const isBlueDart =
        response?.shipment?.shipping_provider?.shipping_provider_type ===
        'BLUEDART';

      if (isBlueDart) {
        generateBlueDartAwbPdf(response);
      } else if (shipmentAWBFile) {
        const awbHtml = await convertPdfToHtml(shipmentAWBFile);
        showAwbPdf(awbHtml, response);
      } else {
        generateInvoicePdf(response);
      }
    } catch (error) {
      toast.error('Error generating PDF');
    }
  };

  const extendedColumns = useMemo(() => {
    const refreshShipmentColumn = {
      accessorKey: 'refresh_shipment',
      header: '',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { id, shipment_status } = original;

        if (shipment_status === 'packed') {
          return (
            <Button
              variant="outline-warning"
              size="sm"
              onClick={() => handleRefreshShipment(id)}
            >
              Regenerate AWB
            </Button>
          );
        }

        return null;
      },
    };

    const invoicePrintColumn = {
      accessorKey: 'none',
      header: '',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const { id, shipment_status } = original;

        if (PRINTABLE_INVOICE_STATUSES.includes(shipment_status)) {
          return (
            <FontAwesomeIcon
              className="cursor-pointer"
              icon="file-alt"
              onClick={() => handlePrintPdf(id)}
            />
          );
        }

        return null;
      },
    };

    return [
      ...getOrderDetailShipmentColumns(tenant, warehouse),
      refreshShipmentColumn,
      invoicePrintColumn,
    ];
  }, [tenant, warehouse]);

  return (
    <Table
      ref={tableRef}
      title={`Order #${orderDetailData?.channelOrderId} Shipments`}
      apiService={shipmentApi}
      columns={extendedColumns}
      fetchConfig={{
        method: 'getById',
        transformResponse: response => ({
          data: response?.results || [],
          totalItems: response?.results?.length || 0,
        }),
      }}
      resourceId={orderId}
      enablePagination={false}
      renderExpandedRow={row => (
        <OrderShipmentExpandedRow shipment={row.original} />
      )}
    />
  );
};

export default OrderShipments;
